import React, { type ReactNode } from 'react';
import { defineMessages, useIntl, type IntlShape } from 'react-intl-next';

import { Box, Flex, xcss } from '@atlaskit/primitives';

import { useKeyboardShortcutsState } from './useKeyboardShortcutsState';

export const isMac = () => window.navigator && window.navigator.userAgent?.indexOf('Mac') > -1;

const i18n = defineMessages({
	keyboardShortcutShift: {
		id: 'shortcuts.shift',
		defaultMessage: 'Shift',
	},
	keyboardShortcutCtrl: {
		id: 'shortcuts.ctrl',
		defaultMessage: 'Ctrl',
	},
	keyboardShortcutCommandWin: {
		id: 'shortcuts.cmd.win',
		defaultMessage: 'Ctrl',
	},
	keyboardShortcutCommandMac: {
		id: 'shortcuts.cmd.mac',
		defaultMessage: 'Cmd',
	},
	keyboardShortcutOptionMac: {
		id: 'shortcuts.opt.mac',
		defaultMessage: 'Opt',
	},
	keyboardShortcutOptionWin: {
		id: 'shortcuts.opt.win',
		defaultMessage: 'Alt',
	},
	keyboardShortcutPlus: {
		id: 'shortcuts.plus',
		defaultMessage: '+',
	},
	keyboardShortcutMinus: {
		id: 'shortcuts.minus',
		defaultMessage: '-',
	},
	keyboardShortcutEnter: {
		id: 'shortcuts.enter',
		defaultMessage: 'Enter',
		description: 'Display of a keyboard shortcut with the enter key on the keyboard',
	},
	keyboardShortcutMeta: {
		id: 'shortcuts.meta',
		defaultMessage: 'Meta',
		description: 'Display of a keyboard shortcut with the windows key on the keyboard',
	},
});

const processKey = (key, formatMessage) => {
	const mac = isMac();
	const lowerCaseKey = key.toLowerCase();

	switch (lowerCaseKey) {
		case 'shift':
			return '\u21E7'; // ⇧
		case 'cmd':
		case 'mod':
			return mac
				? '\u2318' // ⌘
				: formatMessage(i18n.keyboardShortcutCommandWin);
		case 'ctrl':
			return formatMessage(i18n.keyboardShortcutCtrl);
		case 'opt':
		case 'option':
			return mac
				? formatMessage(i18n.keyboardShortcutOptionMac)
				: formatMessage(i18n.keyboardShortcutOptionWin);
		case 'enter':
			return formatMessage(i18n.keyboardShortcutEnter);
		case 'meta':
			return formatMessage(i18n.keyboardShortcutMeta);
		case 'uparrow':
			return '\u2191'; // ↑
		case 'downarrow':
			return '\u2193'; // ↓
		case 'leftarrow':
			return '\u2190'; // ←
		case 'rightarrow':
			return '\u2192'; // →
		case 'plus':
			return '+';
		case 'minus':
			return '-';
		default:
			return key;
	}
};

/**
 * Get a list of strings to be displayed as the keyboard shortcut
 * @param accelerator
 * @returns
 */
export const getShortcutStrings = (
	accelerator: string,
	formatMessage: IntlShape['formatMessage'],
): string[] => {
	const shortcutKeys = accelerator.includes(' ') ? accelerator.split(' ') : accelerator.split('+');
	return shortcutKeys.map((key) => {
		const processedKey = processKey(key, formatMessage);

		if (processedKey.length === 1) {
			return processedKey.toUpperCase();
		} else {
			return processedKey;
		}
	});
};

const shortcutBaseStyle = xcss({
	borderRadius: '2px',
	marginLeft: 'space.025',
	marginRight: 'space.025',
	paddingBlock: 'space.025',
	paddingInline: 'space.100',
});

const shortcutStyleWithContentBefore = xcss({
	marginLeft: 'space.050',
	marginRight: 'space.0',
});

const darkBackgroundStyle = xcss({
	backgroundColor: 'color.background.accent.gray.subtle',
});

const lightBackgroundStyle = xcss({
	backgroundColor: 'color.background.accent.gray.subtlest',
});

type ShortcutVisualizerProps = {
	shortcut: string;
	contentBefore?: ReactNode;
	isEditorShortcut?: boolean;
	isTooltip?: boolean;
};

export const ShortcutVisualizer = ({
	shortcut,
	contentBefore,
	isEditorShortcut,
	isTooltip = true,
}: ShortcutVisualizerProps) => {
	const [areKeyboardShortcutsEnabled] = useKeyboardShortcutsState();
	const intl = useIntl();
	const shortcutStrings = getShortcutStrings(shortcut, intl.formatMessage);

	return (
		<Box paddingBlock="space.025">
			<Flex alignItems="center">
				{contentBefore}
				{(areKeyboardShortcutsEnabled || isEditorShortcut) &&
					shortcutStrings.map((shortcutSegment, index) => (
						<Box
							key={`${shortcutSegment}-${index}`}
							xcss={[
								shortcutBaseStyle,
								!!contentBefore && shortcutStyleWithContentBefore,
								isTooltip ? darkBackgroundStyle : lightBackgroundStyle,
							]}
						>
							{shortcutSegment}
						</Box>
					))}
			</Flex>
		</Box>
	);
};
